import { NgClass } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  effect,
  ElementRef,
  EnvironmentInjector,
  HostListener,
  inject,
  runInInjectionContext,
  signal,
  viewChild,
  viewChildren,
  AfterViewInit,
  OnDestroy,
} from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { FilterChipsService } from '@modules/filter/services/filter-chips/filter-chips.service';
import { FilterService } from '@modules/filter/services/filter/filter.service';
import { FChipsItemComponent } from '../f-chips-item/f-chips-item.component';

@Component({
  selector: 'app-f-chips',
  imports: [NgClass, FChipsItemComponent, MatIconModule],
  templateUrl: './f-chips.component.html',
  styleUrl: './f-chips.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FChipsComponent implements AfterViewInit, OnDestroy {
  private host = inject(ElementRef).nativeElement as HTMLElement;

  private chipsList = viewChildren(FChipsItemComponent);

  private ei = inject(EnvironmentInjector);

  private buttonsContainer = viewChild('buttonsContainer', { read: ElementRef });

  private chipsContainer = viewChild('container', { read: ElementRef });

  @HostListener('window:resize')
  protected resize() {
    this.isOverflowWidth.set(this.calculateOverflowWidth());
  }

  private hostHeight = 32;

  private observer = new IntersectionObserver(
    (entries: IntersectionObserverEntry[]) => {
      this.isOverflowWidth.set(this.calculateOverflowWidth());
      this.greaterOneRow.set(this.host.clientHeight > this.hostHeight);
    },
    { root: this.host, rootMargin: '4px', threshold: 1 },
  );

  private calculateOverflowWidth() {
    if (!this.chipsContainer() || !this.buttonsContainer()) return false;
    const chipsContainerWidth = this.chipsContainer()?.nativeElement.clientWidth;
    const buttonsContainerWidth = this.buttonsContainer()?.nativeElement.clientWidth;
    const parentWidth = this.host.clientWidth;
    const padding = this.getSumHostPadding();

    return Boolean(chipsContainerWidth + buttonsContainerWidth + padding >= parentWidth);
  }

  private getSumHostPadding(): number {
    const style = getComputedStyle(this.host);
    return parseFloat(style.paddingLeft) + parseFloat(style.paddingRight);
  }

  isOverflowWidth = signal(false);

  greaterOneRow = signal(false);

  chipsService = inject(FilterChipsService);

  filterService = inject(FilterService);

  ngAfterViewInit(): void {
    runInInjectionContext(this.ei, () => {
      effect(async () => {
        this.observer.disconnect();
        this.chipsList().forEach((el) => {
          this.observer.observe(el.host);
        });
      });
    });
  }

  ngOnDestroy(): void {
    this.observer.disconnect();
  }
}
